import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {AllowanceEntity, BokkzTslDataService, Kind} from "@snelstart-ventures/fiscaal/domain-logic";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type KindFormGroup = FormGroup<{
  voornaam: FormControl<string | null>;
  geboortedatum: FormControl<Date | null>;
  bsn: FormControl<string | null>;
  ingeschrevenOpAdresAangever: FormControl<boolean | null>;
  ouderOntvangtKinderbijslag: FormControl<boolean | null>;
  onderhoudBelangrijkeMate: FormControl<boolean | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kinderen',
  templateUrl: './kinderen.component.html',
  styleUrls: ['./kinderen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class KinderenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);

  public formGroup = new FormGroup({
    kinderen: new FormArray<KindFormGroup>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: undefined, label: "N.v.t."},
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public itemsInschrijvingAangever: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Wel ingeschreven bij aangever"},
    {value: false, label: "Niet ingeschreven bij aangever"}
  ];

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data.toeslagen?.kinderen || []);
    })
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestAllowances().subscribe();
  }

  get kinderen(): FormArray<KindFormGroup> {
    return this.formGroup.controls.kinderen;
  }

  addRij(): void {
    this.kinderen.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.kinderen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: Kind): FormGroup {
    return new FormGroup({
      voornaam: new FormControl<string | null>(item?.voornaam ?? null),
      geboortedatum: new FormControl<Date | null>(item?.geboortedatum ?? null),
      bsn: new FormControl<string | null>(item?.bsn ?? null),
      ingeschrevenOpAdresAangever: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? null),
      ouderOntvangtKinderbijslag: new FormControl<boolean | null>(item?.ouderOntvangtKinderbijslag ?? null),
      onderhoudBelangrijkeMate: new FormControl<boolean | null>(item?.onderhoudBelangrijkeMate ?? null),
    });
  }

  private updateForm(data : Kind[]): void {
    const lijst = data || [];
    const formArray = this.kinderen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i])
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({ emitEvent: false });

    setTimeout(() => {
      const editedKinderen = this.formGroup.getRawValue().kinderen ?? [];

      this.dataService.patchAllowanceEntity((currentEntity) => {
        const existing = currentEntity.toeslagen?.kinderen ?? [];

        const merged: Kind[] = editedKinderen.map((edited, i) => {
          // drop null fields so `existing[i]` (incl. opvangPerKind) shines through
          const cleaned = Object.fromEntries(
            Object.entries(edited).filter(([, v]) => v !== null),
          );
          return { ...existing[i], ...cleaned } as Kind;
        });

        return {
          ...currentEntity,
          toeslagen: { ...currentEntity.toeslagen, kinderen: merged },
        };
      });
    }, 0);
  }
}
